import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { Button } from '@/design-system/button/button';
import { Inline } from '@/design-system/inline/inline';
import { Stack } from '@/design-system/stack/stack';
import { AccountInvestmentTypeView } from '@/types/entity-types';
import { ArrowDownIcon, ArrowUpRight } from 'lucide-react';
import { useEffect } from 'react';
import { ANNUAL_FEE_PERCENTAGE, TREASURY_HELP_LINK } from '../treasury-page';
import { HoldingDetails } from '../types/treasury-types';
import { ReturnData } from '../utils/get-return-data';
import { AccountBalanceCard } from './account-balance-card';
import { MetricCard } from './metric-card';
import { MoreInfoDropdownMenu } from './more-info-dropdown-menu';
import { ReturnDrawerTriggerCard } from './return-drawer-trigger-card';
import { TreasuryAccountTransactionTableWrapper } from './treasury-account-transaction-table-wrapper';

type TreasuryDashboardProps = {
  account: AccountInvestmentTypeView;
  holdingDetails: HoldingDetails;
  returnData: ReturnData;
  onInitiateDeposit: () => void;
  onInitiateWithdrawal: () => void;
};

export const TreasuryDashboard = ({
  account,
  holdingDetails,
  returnData,
  onInitiateDeposit,
  onInitiateWithdrawal,
}: TreasuryDashboardProps) => {
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.track('treasury_homepage_viewed', {
      currentBalance: account.totalBalance ?? 0,
      currency: account.currency,
      hasBalance: (account.totalBalance ?? 0) > 0,
    });
  }, [analytics, account.currency, account.totalBalance]);

  return (
    <Stack space={6} align="stretch" className="px-6 pt-4">
      <Inline space={6} justify="between" className="gap-y-4">
        <Stack space={1}>
          <h1 className="text-2xl font-semibold text-foreground">Treasury</h1>

          <p className="text-sm font-normal text-muted-foreground">{holdingDetails.name}</p>
        </Stack>

        <Inline space={2} className="gap-y-2">
          <Button variant="default" iconLeft={<ArrowDownIcon />} onClick={onInitiateDeposit}>
            Deposit
          </Button>

          <Button variant="secondary" iconLeft={<ArrowUpRight />} onClick={onInitiateWithdrawal}>
            Withdraw
          </Button>

          <MoreInfoDropdownMenu
            menuItems={[
              {
                variant: 'INVESTOR_INFORMATION',
                onSelect: () => {
                  window.open(holdingDetails.kiidLink, '_blank', 'noopener,noreferrer');
                },
              },
              {
                variant: 'MORE_ABOUT_THE_FUND',
                onSelect: () => {
                  window.open(holdingDetails.moreInfoLink, '_blank', 'noopener,noreferrer');
                },
              },
              {
                variant: 'FAQS',
                onSelect: () => {
                  window.open(TREASURY_HELP_LINK, '_blank', 'noopener,noreferrer');
                },
              },
            ]}
          />
        </Inline>
      </Inline>

      <Inline space={4} align="stretch" className="gap-y-4">
        <AccountBalanceCard
          currency={account.currency}
          accountBalance={account.totalBalance ?? 0}
          pendingDepositsBalance={account.pendingDepositsBalance ?? 0}
          pendingWithdrawalsBalance={account.pendingWithdrawalsBalance ?? 0}
        />

        <Inline space={4} className="flex-1 gap-y-4">
          <MetricCard
            cardConfig={{
              variant: 'YIELD',
              yield: holdingDetails.estimatedYield,
              annualFees: ANNUAL_FEE_PERCENTAGE,
              netRate: holdingDetails.netYield,
            }}
          />

          {returnData.monthlyReturns.length > 0 && (
            <ReturnDrawerTriggerCard
              cardConfig={{
                variant: 'RETURN',
                returnData,
                currency: holdingDetails.currency,
              }}
              onOpenDrawer={() =>
                analytics.track('treasury_returns_viewed', {
                  currentYield: holdingDetails.estimatedYield,
                  currency: account.currency,
                  accountBalance: account.totalBalance ?? 0,
                  monthlyReturn: returnData.monthlyReturns[0].totalReturn,
                })
              }
            />
          )}
        </Inline>
      </Inline>

      <TreasuryAccountTransactionTableWrapper transactions={account.transactions ?? []} />

      <p className="text-sm font-normal text-pretty text-muted-foreground">
        {`Seapoint Treasury is provided by Seapoint Finance UK Investments Ltd. Capital at risk. Variable rate is based on 7-day performance as of `}
        <span className="whitespace-nowrap">{holdingDetails.formattedUpdatedAtByFundManager}</span>
        {`.`}
      </p>
    </Stack>
  );
};
